import React, { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { LuBell } from "react-icons/lu";
import { readNotificationPermissionState, requestForToken } from "../firebase";

const isNativeAndroid = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";

// A secondary, non-blocking entry point. The successful-login modal keeps its existing flow.
const NativeNotificationPrompt = ({ accessToken }) => {
    const [permission, setPermission] = useState("unsupported");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!isNativeAndroid || !accessToken) {
            setPermission("unsupported");
            return undefined;
        }

        let active = true;
        const refreshPermission = async () => {
            const current = await readNotificationPermissionState();
            if (active) setPermission(current);
        };

        refreshPermission();
        // Recheck if the user changes permission in Android settings and returns.
        const onVisible = () => {
            if (document.visibilityState === "visible") refreshPermission();
        };
        window.addEventListener("focus", refreshPermission);
        window.addEventListener("marankoo:notification-permission-updated", refreshPermission);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            active = false;
            window.removeEventListener("focus", refreshPermission);
            window.removeEventListener("marankoo:notification-permission-updated", refreshPermission);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [accessToken]);

    const enableNotifications = async () => {
        if (loading || !accessToken) return;
        setLoading(true);
        setError("");
        try {
            // Reuse the existing native permission + FCM token + backend registration path.
            const token = await requestForToken(accessToken, { requestPermission: true });
            const current = await readNotificationPermissionState();
            setPermission(current);
            if (!token && current === "granted") {
                setError("مجوز داده شده، اما ثبت دستگاه انجام نشد. اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.");
            }
        } catch (_) {
            setError("فعال‌سازی اعلان‌ها انجام نشد. دوباره تلاش کنید.");
        } finally {
            setLoading(false);
        }
    };

    const canRequest = permission === "prompt" || permission === "prompt-with-rationale";
    const canRetry = permission === "granted" && Boolean(error);
    if (!isNativeAndroid || !accessToken || (!canRequest && !canRetry && permission !== "denied")) return null;

    return (
        <section
            className="mx-auto mt-5 w-[calc(100%-32px)] max-w-[980px] rounded-[14px] border border-[#D6E1F0] bg-[#F8FAFC] px-4 py-4"
            dir="rtl"
            aria-label="تنظیمات اعلان‌های سفر"
        >
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                    <LuBell size={23} className="mt-1 shrink-0 text-[#082F78]" aria-hidden="true" />
                    <div>
                        <p className="text-[14px] font-semibold text-[#0A192F]">اعلان‌های سفر را از دست ندهید</p>
                        <p className="mt-1 text-[12px] leading-6 text-[#536174]">
                            {canRequest
                                ? "برای اطلاع از پذیرش راننده و تغییر وضعیت سفر، اعلان‌های مارانکو را فعال کنید."
                                : canRetry
                                    ? "دسترسی اعلان‌ها فعال است؛ برای تکمیل ثبت این دستگاه دوباره تلاش کنید."
                                    : "اعلان‌ها غیرفعال‌اند. برای فعال‌سازی به تنظیمات گوشی ← برنامه‌ها ← مارانکو ← اعلان‌ها بروید."}
                        </p>
                    </div>
                </div>
                {(canRequest || canRetry) && (
                    <button
                        type="button"
                        onClick={enableNotifications}
                        disabled={loading}
                        className="min-h-[44px] shrink-0 rounded-[10px] bg-[#082F78] px-5 py-2 text-[13px] font-semibold text-white disabled:opacity-60"
                    >
                        {loading ? "در حال فعال‌سازی..." : canRetry ? "تلاش مجدد" : "فعال‌سازی اعلان‌ها"}
                    </button>
                )}
            </div>
            {error && <p role="alert" className="mt-2 text-[12px] text-red-700">{error}</p>}
        </section>
    );
};

export default NativeNotificationPrompt;
